
angular.module('utils')
  .factory(
    'OCTrackedModals',
    function(
      $state,
      OCLogUtil,
      CurrentUser,
      ClinicService,
      OCModalUtil,
      GrowlService,
      OCStringUtil,
    ) {
      const COUNTRIES = {
        UNITED_STATES: 'US',
        CANADA: 'CA',
      };

      const {
        showCustomModal,
        createResolve,
      } = OCModalUtil;

      const createOpenCareTipModal = ({
        name,
        header,
        images,
        contentTemplate,
        clickToAdvanceText,
        onClickToAdvance,
        country,
      }) => ({
        name,
        onClickToAdvance,
        country,
        getFrequency: () => 'once',
        bodyTemplateUrl: '/views/modals/tracked/_opencare-tip-modal.html',
        modalInfo: {
          windowClass: `opencare-tip-modal ${name}`,
        },
        customResolve: () =>
          function() {
            return {
              header,
              images,
              contentTemplate,
              clickToAdvanceText,
            };
          },
      });

      /**
      * The template has access to the scope of the `TrackedModalWrapperControl` controller
      * Notably the following scope variable can and **should** be used in the template:
      *    close: A function to close the modal with no action
      *    advance: A function to call when the user clicks-to-advance.
      *             This will call the onClickToAdvance function after network calls.
      *    loading: A boolean is true after `advance` has been called but before it finishes
      *             `advance` makes network calls so this should be used for best UX
      * @type {{
      *  name : string;
      *  frequency : () => 'once' | 'daily' | 'weekly' | 'monthly';
      *  country : 'US' | 'CA' | null
      *  bodyTemplateUrl : string;
      *  showCloseButton : bool?;
      *  onClickToAdvance : () => null?;
      *  customResolve : { [string] : any }?;
      *  modalInfo : { [string] : any }?;
      * }[]}
      **/
      const trackedModals = [

        // Modal for clincis to adopt the lead time feature
        createOpenCareTipModal({
          name: 'leadTimeAdoptionModal',
          header: 'Receiving last minute appointment requests?',
          contentTemplate: '/views/modals/tracked/_lead-time-feature-modal-content.html',
          images: [
            {
              src: '/img/product-calendar-tab.png',
              alt: 'Never Miss an Appointment Request Again!',
            },
            {
              src: '/img/product-lead-time.png',
              alt: 'Never Miss an Appointment Request Again!',
            },
          ],
          onClickToAdvance: () =>
            $state.go('clinic.timeslots', {
              tab: 'calendar',
            }),
        }),

        // Modal to inform users about the new calendar preview feature
        createOpenCareTipModal({
          name: 'calendar-preview-adoption',
          header: 'Need an easy way to review your calendar?',
          contentTemplate: '/views/modals/tracked/_calendar-preview-adoption-modal-content.html',
          images: [
            '/img/calendar-preview-adoption/setting-location.png',
            '/img/calendar-preview-adoption/modal-example.png',
          ],
          onClickToAdvance: () => $state.go('clinic.settings'),
        }),

        // Modal to inform users about the March 2024 new insurance data update
        createOpenCareTipModal({
          name: 'insurance-data-update',
          header: "We've added new insurance providers and plans!",
          country: COUNTRIES.UNITED_STATES,
          clickToAdvanceText: 'Update now!',
          contentTemplate: '/views/modals/tracked/_insurance-data-update-modal-content.html',
          images: [
            '/img/insurance-update-modal/aetna.png',
            '/img/insurance-update-modal/delta-dental.png',
            '/img/insurance-update-modal/cigna.png',
          ],
          onClickToAdvance: ({ clinic }) => {
            const doctorId = CurrentUser.getAllOnlineBookingDoctors(clinic.id)[0]?.id;

            if (!doctorId) {
              GrowlService.error('There are no providers set up.');
              return;
            }

            $state.go('doctor.insurance', {
              id: doctorId,
            }, {
              reload: true,
            });
          },
        }),

        // Modal to inform users to update safety procedures
        createOpenCareTipModal({
          name: 'safety-procedure-update',
          header: 'Update Practice Profile for Updated Safety Protocols',
          contentTemplate: '/views/modals/tracked/_safety-procedure-update-modal-content.html',
          images: [
            '/img/safety-procedures-update-modal/safety-procedures-edit.png',
          ],
          onClickToAdvance: () => $state.go('clinic.profile', {
            section: 'safety-procedures',
          }),
        }),

        // Recurring modal for previewing calendar
        {
          name: 'calendar-preview',
          getFrequency: () => CurrentUser.getUser().metadata?.calendarPreviewFrequency || 'never',
          bodyTemplateUrl: '/views/modals/tracked/_calendar-preview-modal.html',
          showCloseButton: true,
          onClickToAdvance: () =>
            $state.go('clinic.timeslots', {
              tab: 'calendar',
            }),
          customResolve: (clinic) =>
            function() {
              let availableSlotsByDay = [...Array(7).keys()]
                .map((index) => moment().add(index, 'days')
                  .startOf('day'));

              const getSlotsOnDay = (date, slots) =>
                slots
                  .filter((slot) => date.isSame(slot.date, 'day'))
                  .map((slot) => ({
                    ...slot,
                    date: moment(date).utc()
                      .startOf('day')
                      .add(slot.time, 'minutes'),
                  }))
                  .sort((a, b) => a.time - b.time);

              return ClinicService
                .getOpenSlots(clinic.id)
                .then((slots) => {
                  availableSlotsByDay = availableSlotsByDay
                    .map((date) => ({
                      date,
                      slots: getSlotsOnDay(date, slots),
                    }));
                  return {
                    availableSlotsByDay,
                    noSlots: availableSlotsByDay.every((day) => day.slots.length === 0),
                  };
                });
            },
          modalInfo: {
            windowClass: 'calendar-preview-modal',
            size: 'lg',
          },
        },

        // Modal to inform users about the new optional member id feature
        createOpenCareTipModal({
          name: 'optional-member-id',
          header: "We've added a new feature!",
          country: COUNTRIES.CANADA,
          clickToAdvanceText: 'Check it out!',
          contentTemplate: '/views/modals/tracked/_optional-member-id-modal-content.html',
          images: [
            '/img/optional-member-id-modal/optional-member-id.png',
          ],
          onClickToAdvance: () => $state.go('clinic.profile', {
            section: 'optional-member-id',
          }),
        }),
      ];

      const shouldShowTrackedModal = ({ getFrequency, name, country }, clinic) => {
        if (country) {
          const countryMap = {
            CA: OCStringUtil.isCanada,
            US: OCStringUtil.isUnitedStates,
          };
          const clinicCountryCode = Object.keys(countryMap)
            .find((countryCode) => {
              const isCountry = countryMap[countryCode];
              return isCountry(clinic.address.country);
            });
          if (clinicCountryCode !== country) {
            return false;
          }
        }

        const validFrequencies = {
          once: () => null, // Never show after the first time
          daily: (lastViewTime) =>
            moment(lastViewTime)
              .add(1, 'days')
              .startOf('day'),
          weekly: (lastViewTime) =>
            moment(lastViewTime)
              .add(7, 'days')
              .startOf('week'),
          monthly: (lastViewTime) =>
            moment(lastViewTime)
              .add(1, 'months')
              .startOf('month'),
        };

        const frequency = getFrequency();

        if (frequency === 'never') {
          return false;
        }

        const getModalShowDate = validFrequencies[frequency];

        if (!getModalShowDate) {
          const message = `Invalid frequency for tracked modal: ${frequency}`;
          OCLogUtil.rollbar.error(message);
          throw new Error(message);
        }

        const user = CurrentUser.getUser();
        const lastViewed = user.modalTrackingInfo?.[name]?.viewedAt;

        const today = moment();
        return (
          // The user has never seen this modal
          !lastViewed

          // The date to show the modal has passed
          || getModalShowDate(moment(lastViewed))?.isBefore(today)
        );
      };

      const showTrackedModalIfApplicable = (clinic) => {

        const trackedModal = trackedModals
          .find((modal) => shouldShowTrackedModal(modal, clinic));

        if (!trackedModal) {
          return Promise.resolve();
        }

        const {
          name,
          bodyTemplateUrl,
          onClickToAdvance,
          customResolve,
          modalInfo,
          showCloseButton,
        } = trackedModal;

        return showCustomModal({
          ...modalInfo,
          windowClass: `tracked-modal-wrapper ${modalInfo.windowClass ?? ''}`,
          templateUrl: '/views/modals/tracked/_tracked-modal-wrapper.html',
          controller: 'TrackedModalWrapperControl',
          resolve: {
            customResolve: customResolve?.(clinic) ?? (() => ({})),
            ...createResolve({
              clinic,
              modalName: name,
              bodyTemplateUrl,
              onClickToAdvance,
              showCloseButton,
            }),
          },
        });
      };

      return {
        showTrackedModalIfApplicable,
      };
    },
  );
